import { Button } from "components/DesignSystem/Button/Button";
import { RightArrow } from "components/icons/RightArrow";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { joinLegalName } from "v2/utils/joinLegalName";
import { getEntityTypeLabel } from "v2/utils/getEntityTypeLabel";
import {
  AddressData,
  EntityRelation,
  EntityReviewItem,
  OnboardingEntityInput,
  useAddGroupEntityMutation,
  useCreateOnboardingEntityMutation,
  useDeleteOnboardingEntityMutation,
  useGetGroupReviewDetailsQuery,
  useGetOnboardingPrefillQuery,
  useUpdateOnboardingEntityMutation,
  useVerifyGroupEntitiesMutation,
} from "v2/store/onboardingEntityApi";
import { useUpdateGroupNameMutation } from "store/apis/group";
import {
  EntityRelationKind,
  OnboardingEntityDraft,
  useOnboardingData,
} from "../../OnboardingDataContext";
import { SOMETHING_WENT_WRONG } from "constants/apiCallError";
import {
  AddAnotherEntityDropdown,
  AddEntityMode,
} from "./AddAnotherEntityDropdown";
import { AddEntityForm } from "./AddEntityForm";
import { BrandNameBox } from "./BrandNameBox";
import { buildEntityTree } from "./buildEntityTree";
import { EntitySubtree } from "./EntitySubtree";
import { buildConfirmEntitySummary } from "./confirmEntitySummary";
import { CompanyValues, PrimaryEntityForm } from "./PrimaryEntityForm";
import { RELATION_TYPE } from "./relationConstants";
import { RemoveEntityModal } from "./RemoveEntityModal";
import {
  getDefaultSuffix,
  getFormationTypeLabel,
  getPrefixesForType,
} from "./entityFieldOptions";

type ConfirmEntityStepProps = {
  onComplete: (summary: ReactNode) => void;
  onError: () => void;
  onJoinExisting?: () => void;
};

type PrimaryEntity = {
  companyName: string;
  entityName: string;
  entitySuffix: string;
  formationTypeId: string;
  entityTypeLabel: string;
  taxedAs?: string;
  addressData: AddressData | null;
};

const PREFILL_POLL_INTERVAL_MS = 3000;
const PREFILL_POLL_OFF = 0;

const toEntityInput = (
  entity: Pick<
    PrimaryEntity,
    | "formationTypeId"
    | "entityName"
    | "entitySuffix"
    | "taxedAs"
    | "addressData"
  >
): OnboardingEntityInput => ({
  formationTypeId: entity.formationTypeId,
  entityName: entity.entityName.trim(),
  entitySuffix: entity.entitySuffix,
  addressData: entity.addressData ?? undefined,
  taxedAs: entity.taxedAs,
});

const reviewToDraft = (
  item: EntityReviewItem,
  local: OnboardingEntityDraft | undefined,
  isPrimary: boolean
): OnboardingEntityDraft => {
  const parentRel = item.relatedEntities.find(
    (related) => related.relation.toLowerCase() === RELATION_TYPE.parent
  );
  const subsidiaryRel = item.relatedEntities.find(
    (related) => related.relation.toLowerCase() === RELATION_TYPE.subsidiary
  );
  let derivedKind: EntityRelationKind | undefined;
  if (parentRel) derivedKind = "subsidiary";
  else if (subsidiaryRel) derivedKind = "parent";
  return {
    localId: local?.localId ?? item.entityId,
    entityId: item.entityId,
    registeredName: item.registeredName || local?.registeredName,
    entityName: item.entityName || item.registeredName,
    entitySuffix: item.entitySuffix,
    entityTypeLabel: item.entityTypeLabel,
    formationTypeId: item.formationTypeId || local?.formationTypeId || "",
    taxedAs: item.taxedAs ?? local?.taxedAs,
    addressData: item.addressData ?? local?.addressData ?? null,
    isPrimary,
    relationKind: derivedKind ?? local?.relationKind,
    relatedEntityId: (parentRel ?? subsidiaryRel)?.id ?? local?.relatedEntityId,
  };
};

export const ConfirmEntityStep = ({
  onComplete,
  onError,
  onJoinExisting,
}: ConfirmEntityStepProps) => {
  const { data, patchData } = useOnboardingData();
  const { alertToast, successToast } = useToast();
  const [createOnboardingEntity, { isLoading: isCreating }] =
    useCreateOnboardingEntityMutation();
  const [updateOnboardingEntity, { isLoading: isUpdating }] =
    useUpdateOnboardingEntityMutation();
  const [updateGroupName, { isLoading: isRenamingGroup }] =
    useUpdateGroupNameMutation();
  const [addGroupEntity, { isLoading: isAdding }] = useAddGroupEntityMutation();
  const [verifyGroupEntities] = useVerifyGroupEntitiesMutation();
  const [deleteOnboardingEntity, { isLoading: isDeleting }] =
    useDeleteOnboardingEntityMutation();

  const isSavingEntity =
    isCreating || isUpdating || isAdding || isRenamingGroup || isDeleting;

  const [primary, setPrimary] = useState<PrimaryEntity | null>(null);
  const [isEditingPrimary, setIsEditingPrimary] = useState(false);
  const [separateEntities, setSeparateEntities] = useState<
    OnboardingEntityDraft[]
  >(() => data.entities.filter((entity) => !entity.isPrimary));
  const [addForm, setAddForm] = useState<{
    open: boolean;
    editing: OnboardingEntityDraft | null;
    mode: AddEntityMode;
  }>({ open: false, editing: null, mode: "separate" });
  const deleteModal = useModal();
  const [pendingDelete, setPendingDelete] =
    useState<OnboardingEntityDraft | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);

  const domain = data.email.split("@")[1] ?? "";
  const [prefillInterval, setPrefillInterval] = useState(
    PREFILL_POLL_INTERVAL_MS
  );
  const { data: prefill } = useGetOnboardingPrefillQuery(
    { domain, formationTypeId: data.entityTypeId },
    { skip: !domain, pollingInterval: prefillInterval }
  );

  if (
    prefill &&
    !prefill.verificationPending &&
    prefillInterval !== PREFILL_POLL_OFF
  ) {
    setPrefillInterval(PREFILL_POLL_OFF);
  }

  const { data: countries } = useGetCountriesDataQuery();

  const { data: groupReview, refetch: refetchReview } =
    useGetGroupReviewDetailsQuery(
      { groupId: data.groupId },
      { skip: !data.groupId, refetchOnMountOrArgChange: true }
    );

  const entitiesRef = useRef(data.entities);
  entitiesRef.current = data.entities;
  const patchDataRef = useRef(patchData);
  patchDataRef.current = patchData;

  useEffect(() => {
    if (!groupReview || groupReview.length === 0) return;
    const localById = new Map(
      entitiesRef.current.map((entity) => [entity.entityId, entity])
    );
    const drafts = groupReview.map((item, index) =>
      reviewToDraft(item, localById.get(item.entityId), index === 0)
    );
    patchDataRef.current({ entities: drafts });
    setSeparateEntities(drafts.filter((draft) => !draft.isPrimary));
  }, [groupReview]);

  const prefixes = getPrefixesForType(countries, data.entityTypeId);
  const suffixOptions = prefixes.map((prefix) => prefix.prefix);
  const defaultSuffix = getDefaultSuffix(prefixes);
  const savedSuffix = data.entitySuffix || prefill?.entitySuffix || "";
  const initialSuffix = suffixOptions.includes(savedSuffix)
    ? savedSuffix
    : defaultSuffix;

  const prefillAddress = prefill?.addressData ?? null;
  const hasAllPrefillData = Boolean(
    prefill?.entityName &&
      prefillAddress?.street_address &&
      prefillAddress?.city
  );

  const prefillPrimary: PrimaryEntity | null = hasAllPrefillData
    ? {
        companyName:
          data.companyName || prefill?.brandName || prefill?.entityName || "",
        entityName: data.entityName || prefill?.entityName || "",
        entitySuffix: initialSuffix,
        formationTypeId: data.entityTypeId,
        entityTypeLabel: getEntityTypeLabel(
          getFormationTypeLabel(countries, data.entityTypeId) ||
            data.entityTypeLabel,
          data.entityTypeTaxedAs,
          data.entityTypeId
        ),
        taxedAs: data.entityTypeTaxedAs || undefined,
        addressData: prefillAddress,
      }
    : null;

  const savedPrimaryDraft = data.entities.find((entity) => entity.isPrimary);
  const savedPrimary: PrimaryEntity | null = savedPrimaryDraft
    ? {
        companyName: data.companyName,
        entityName: savedPrimaryDraft.entityName,
        entitySuffix: savedPrimaryDraft.entitySuffix,
        formationTypeId: savedPrimaryDraft.formationTypeId,
        entityTypeLabel: getEntityTypeLabel(
          getFormationTypeLabel(countries, savedPrimaryDraft.formationTypeId) ||
            savedPrimaryDraft.entityTypeLabel,
          savedPrimaryDraft.taxedAs ?? "",
          savedPrimaryDraft.formationTypeId
        ),
        taxedAs: savedPrimaryDraft.taxedAs,
        addressData: savedPrimaryDraft.addressData,
      }
    : null;

  const effectivePrimary = primary ?? savedPrimary ?? prefillPrimary;
  const isFromPrefill = !primary && !savedPrimary && Boolean(prefillPrimary);
  const showManualForm = !data.groupId || isEditingPrimary;

  const derivedCompanyName =
    effectivePrimary?.companyName ||
    data.companyName ||
    prefill?.brandName ||
    prefill?.entityName ||
    "";
  const effectiveCompanyName = companyName ?? derivedCompanyName;

  const knownFormationTypeId =
    effectivePrimary?.formationTypeId || data.entityTypeId;

  const manualInitialValues: CompanyValues = {
    companyName: effectiveCompanyName,
    formationTypeId: knownFormationTypeId,
    taxedAs: effectivePrimary?.taxedAs || data.entityTypeTaxedAs || "",
    entityName: effectivePrimary?.entityName || "",
    entitySuffix: effectivePrimary?.entitySuffix || initialSuffix,
    streetAddress: effectivePrimary?.addressData?.street_address || "",
    addressLine2: effectivePrimary?.addressData?.street_address_line_2 || "",
    city: effectivePrimary?.addressData?.city || "",
    state: effectivePrimary?.addressData?.state || "",
    country: effectivePrimary?.addressData?.country || "",
    zipcode: effectivePrimary?.addressData?.zipcode || "",
  };

  const isGroupCreated = Boolean(data.groupId);

  const toastError = (error: unknown) =>
    alertToast({
      message:
        (error as { data?: { error?: { message?: string } } })?.data?.error
          ?.message ?? SOMETHING_WENT_WRONG,
    });

  const relationOptions = data.entities
    .filter(
      (entity): entity is OnboardingEntityDraft & { entityId: string } =>
        Boolean(entity.entityId) && entity.localId !== addForm.editing?.localId
    )
    .map((entity) => ({
      value: entity.entityId,
      label: joinLegalName(entity.entityName, entity.entitySuffix) || "Entity",
    }));

  const buildRelation = (
    selfId: string,
    draft: OnboardingEntityDraft
  ): EntityRelation | undefined => {
    if (!draft.relationKind || !draft.relatedEntityId) return undefined;
    return draft.relationKind === "subsidiary"
      ? { parentId: draft.relatedEntityId, childId: selfId }
      : { parentId: selfId, childId: draft.relatedEntityId };
  };

  const handleManualSave = async (values: CompanyValues) => {
    const nextCompanyName = isGroupCreated
      ? effectiveCompanyName
      : values.companyName.trim();
    if (!isGroupCreated) setCompanyName(nextCompanyName);

    const isStepOneEntityType = values.formationTypeId === data.entityTypeId;
    const taxedAs =
      values.taxedAs || (isStepOneEntityType ? data.entityTypeTaxedAs : "");

    const entityTypeLabel = getEntityTypeLabel(
      getFormationTypeLabel(countries, values.formationTypeId),
      taxedAs,
      values.formationTypeId
    );

    const next: PrimaryEntity = {
      companyName: nextCompanyName,
      entityName: values.entityName.trim(),
      entitySuffix: values.entitySuffix,
      formationTypeId: values.formationTypeId,
      entityTypeLabel,
      taxedAs: taxedAs || undefined,
      addressData: {
        street_address: values.streetAddress,
        street_address_line_2: values.addressLine2,
        city: values.city,
        state: values.state,
        zipcode: values.zipcode,
        country: values.country,
      },
    };
    setPrimary(next);
    setIsEditingPrimary(false);

    if (values.formationTypeId !== data.entityTypeId) {
      patchData({
        entityTypeId: values.formationTypeId,
        entityTypeLabel,
        entityTypeTaxedAs: taxedAs,
      });
    }

    if (!isGroupCreated) {
      try {
        const created = await createOnboardingEntity({
          companyName: nextCompanyName,
          entity: toEntityInput(next),
        }).unwrap();
        patchData({
          groupId: created.groupId,
          companyName: nextCompanyName,
          entityId: created.entityId,
          entityName: next.entityName,
          entitySuffix: next.entitySuffix,
          entities: [
            {
              localId: "primary",
              entityId: created.entityId,
              entityName: next.entityName,
              entitySuffix: next.entitySuffix,
              formationTypeId: next.formationTypeId,
              entityTypeLabel: next.entityTypeLabel,
              taxedAs: next.taxedAs,
              addressData: next.addressData,
              isPrimary: true,
            },
          ],
        });
      } catch (error) {
        toastError(error);
      }
      return;
    }

    const entityId = savedPrimaryDraft?.entityId;
    if (!entityId) return;
    try {
      await updateOnboardingEntity({
        entityId,
        input: toEntityInput(next),
        relation: buildRelation(entityId, savedPrimaryDraft),
      }).unwrap();
      patchData({
        entityName: next.entityName,
        entitySuffix: next.entitySuffix,
        entities: data.entities.map((entity) =>
          entity.isPrimary
            ? {
                ...entity,
                entityId,
                entityName: next.entityName,
                entitySuffix: next.entitySuffix,
                formationTypeId: next.formationTypeId,
                entityTypeLabel: next.entityTypeLabel,
                taxedAs: next.taxedAs,
                addressData: next.addressData,
              }
            : entity
        ),
      });
      await refetchReview();
    } catch (error) {
      toastError(error);
    }
  };

  const handleBrandChange = async (name: string) => {
    setCompanyName(name);
    if (!isGroupCreated) return;
    try {
      await updateGroupName({
        groupId: data.groupId,
        name,
      }).unwrap();
      await refetchReview();
    } catch (error) {
      toastError(error);
    }
  };

  const handleEditPrimary = () => setIsEditingPrimary(true);

  const handleAdd = (mode: AddEntityMode) =>
    setAddForm({ open: true, editing: null, mode });

  const handleEditSeparate = (entity: OnboardingEntityDraft) =>
    setAddForm({
      open: true,
      editing: entity,
      mode: entity.relationKind ?? "separate",
    });

  const handleRequestRemove = (entity: OnboardingEntityDraft) => {
    setPendingDelete(entity);
    deleteModal.open();
  };

  const handleCancelDelete = () => {
    deleteModal.close();
    setPendingDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      if (pendingDelete.entityId) {
        await deleteOnboardingEntity({
          entityId: pendingDelete.entityId,
        }).unwrap();
      }
      const next = separateEntities.filter(
        (entity) => entity.localId !== pendingDelete.localId
      );
      setSeparateEntities(next);
      patchData({
        entities: [
          ...data.entities.filter((entity) => entity.isPrimary),
          ...next,
        ],
      });
      successToast({
        message: `${joinLegalName(
          pendingDelete.entityName,
          pendingDelete.entitySuffix
        )} removed`,
      });
      handleCancelDelete();
      await refetchReview();
    } catch (error) {
      toastError(error);
    }
  };

  const handleSaveSeparate = async (draft: OnboardingEntityDraft) => {
    try {
      let entityId = draft.entityId;
      if (entityId) {
        await updateOnboardingEntity({
          entityId,
          input: toEntityInput(draft),
          relation: buildRelation(entityId, draft),
        }).unwrap();
      } else {
        const added = await addGroupEntity({
          groupId: data.groupId,
          input: toEntityInput(draft),
        }).unwrap();
        entityId = added.entityId;
        const relation = buildRelation(entityId, draft);
        if (relation) {
          await updateOnboardingEntity({
            entityId,
            input: toEntityInput(draft),
            relation,
          }).unwrap();
        }
      }

      const savedDraft = { ...draft, entityId };
      const exists = separateEntities.some(
        (entity) => entity.localId === draft.localId
      );
      const nextList = exists
        ? separateEntities.map((entity) =>
            entity.localId === draft.localId ? savedDraft : entity
          )
        : [...separateEntities, savedDraft];
      setSeparateEntities(nextList);
      patchData({
        entities: [
          ...data.entities.filter((entity) => entity.isPrimary),
          ...nextList,
        ],
      });
      successToast({
        message: `${joinLegalName(draft.entityName, draft.entitySuffix)} ${
          draft.entityId ? "updated" : "added"
        }`,
      });
      setAddForm({ open: false, editing: null, mode: "separate" });
      await refetchReview();
    } catch (error) {
      toastError(error);
    }
  };

  const handleCancelSeparate = () =>
    setAddForm({ open: false, editing: null, mode: "separate" });

  const confirmSummary = () =>
    buildConfirmEntitySummary(
      effectiveCompanyName,
      joinLegalName(
        effectivePrimary?.entityName ?? "",
        effectivePrimary?.entitySuffix ?? ""
      ),
      separateEntities.length > 0
        ? `+${separateEntities.length} more`
        : undefined
    );

  const handleConfirm = async () => {
    if (!isGroupCreated) return;
    patchData({ entitiesUpdating: true });
    try {
      onComplete(confirmSummary());
      await verifyGroupEntities({ groupId: data.groupId }).unwrap();
    } catch (error) {
      onError();
      toastError(error);
    } finally {
      patchData({ entitiesUpdating: false });
    }
  };

  const entityTree = buildEntityTree(data.entities);

  const isEditingSeparate = addForm.open && Boolean(addForm.editing);

  return (
    <div className="t-flex t-flex-col t-gap-5">
      {isFromPrefill && (
        <p className="t-m-0 t-text-body-sm t-text-text-30">
          ✨ Identified by Inkle AI based on your email address
        </p>
      )}

      {isGroupCreated ? (
        <BrandNameBox
          companyName={effectiveCompanyName}
          onChange={(name) => void handleBrandChange(name)}
        />
      ) : (
        !showManualForm && (
          <div className="t-flex t-items-center t-gap-2">
            <span className="t-text-body t-text-neutral-40">Brand name:</span>
            <span className="t-text-body t-font-medium t-text-text-60">
              {effectiveCompanyName}
            </span>
          </div>
        )
      )}

      {showManualForm ? (
        <PrimaryEntityForm
          countries={countries}
          initialValues={manualInitialValues}
          canGoBack={isEditingPrimary}
          showCompanyName={!isGroupCreated}
          isFormationTypeLocked={Boolean(knownFormationTypeId)}
          isSaving={isCreating || isUpdating}
          onSave={handleManualSave}
          onBack={() => setIsEditingPrimary(false)}
        />
      ) : isEditingSeparate ? (
        <AddEntityForm
          initial={addForm.editing}
          mode={addForm.mode}
          relationOptions={relationOptions}
          countries={countries}
          onSave={handleSaveSeparate}
          onCancel={handleCancelSeparate}
        />
      ) : (
        <div className="t-flex t-flex-col t-gap-4">
          {entityTree.map((root) => (
            <EntitySubtree
              key={root.entity.localId}
              node={root}
              countries={countries}
              onEditPrimary={handleEditPrimary}
              onEdit={handleEditSeparate}
              onRemove={handleRequestRemove}
            />
          ))}

          {isGroupCreated &&
            (addForm.open ? (
              <AddEntityForm
                initial={addForm.editing}
                mode={addForm.mode}
                relationOptions={relationOptions}
                countries={countries}
                onSave={handleSaveSeparate}
                onCancel={handleCancelSeparate}
              />
            ) : (
              <AddAnotherEntityDropdown onAdd={handleAdd} />
            ))}
        </div>
      )}

      <div className="t-flex t-items-center t-gap-3 sm:t-flex-col sm:t-items-stretch">
        <Button
          type="button"
          customType="primary"
          rightIcon={<RightArrow />}
          onClick={() => void handleConfirm()}
          disabled={!isGroupCreated || isSavingEntity}
        >
          Confirm &amp; fetch further details
        </Button>
        {!isGroupCreated && onJoinExisting && (
          <Button type="button" customType="ghost" onClick={onJoinExisting}>
            Join existing brand
          </Button>
        )}
      </div>

      <RemoveEntityModal
        open={deleteModal.isOpen}
        entityName={
          pendingDelete
            ? joinLegalName(
                pendingDelete.entityName,
                pendingDelete.entitySuffix
              )
            : "this entity"
        }
        isRemoving={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
